/* eslint-disable */
import { useState, useEffect } from 'react';
import { getClientId } from '../hookServices/clientId';
import { HOST, PORT } from '../environment';

export function usePortStatus(portNumber) {
  const [portStatus, setPortStatus] = useState({
    isReady: false,
    isOccupied: false,
    isInProgress: false,
    isCreated: false,
    powerKw: null,
    leftSeconds: null,
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!portNumber) return undefined;

    let cancelled = false;
    let inFlightController = null;

    const fetchPortStatus = async () => {
      inFlightController?.abort();
      const abortController = new AbortController();
      inFlightController = abortController;
      const { signal } = abortController;

      try {
        if (!cancelled) {
          setIsLoading(true);
        }
        const clientId = getClientId();

        // Fetch both APIs in parallel
        const [statusResponse, deviceResponse] = await Promise.all([
          fetch(`${HOST}:${PORT}/api/device/v2/station/status?station_number=${portNumber}&clientUiId=${clientId}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            signal,
          }),
          fetch(`${HOST}:${PORT}/api/device/v2/device/status?station_number=${portNumber}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            signal,
          }),
        ]);

        let statusData = null;
        let deviceData = null;

        if (statusResponse.ok) {
          const contentType = statusResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            statusData = await statusResponse.json();
          }
        }

        if (deviceResponse.ok) {
          const contentType = deviceResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            deviceData = await deviceResponse.json();
          }
        }

        if (cancelled || signal.aborted) {
          return;
        }

        const isInProgress = statusData?.lastJob?.state === 'IN_PROGRESS' || false;
        const powerWt = Number(statusData?.lastJob?.powerWt);
        const powerKw = isInProgress ? (Number.isFinite(powerWt) && powerWt > 0 ? powerWt / 1000 : 0) : null;
        const leftRaw = Number(statusData?.lastJob?.leftS);
        const leftSeconds = isInProgress && Number.isFinite(leftRaw) && leftRaw >= 0 ? Math.floor(leftRaw) : null;

        setPortStatus(prev => ({
          ...prev,
          isReady: deviceData?.isReadyToStart || deviceData?.readyToStart || false,
          isOccupied: Boolean(deviceData?.isOccupied || deviceData?.occupied),
          isInProgress,
          isCreated: statusData?.lastJob?.state === 'CREATED' || false,
          powerKw,
          leftSeconds,
        }));
      } catch (error) {
        if (error?.name === 'AbortError' || cancelled) {
          return;
        }
        console.error(`Error fetching port status for ${portNumber}:`, error);
      } finally {
        if (!cancelled && !signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    // Fetch initial status
    fetchPortStatus();

    // Set up polling every 5 seconds
    const interval = setInterval(fetchPortStatus, 5000);

    return () => {
      cancelled = true;
      clearInterval(interval);
      inFlightController?.abort();
    };
  }, [portNumber]);

  return { portStatus, isLoading };
}
